import {useMutation, useQueryClient} from '@github-ui/react-query'
import type {APITask} from '../types/api-task'
import type {OptimisticTask} from '../types/task'
import type {FlatTaskList} from './use-create-api-task'
import {fetchCAPI, FetchCAPIError} from '../utils/fetch-capi'
import {sortTasksByDate, transformAPITaskToUnhydrated} from '../utils/task-helpers'
import {uuid} from '../utils/uuid'

export type StartLocalDeviceSessionInput = {
  /** The connected local device to dispatch the new task to. */
  deviceId: string
  /** Working directory of the target session on the device. Combined with
   * `deviceId` to identify which local runtime the prompt should land on. */
  workingDirectory: string
  problemStatement: string
}

export type StartLocalDeviceSessionResult = {
  /** Full task object returned by Mission Control. The local device will
   * attach its spawned session to this same task when it registers with
   * Mission Control. */
  task: APITask
}

/**
 * Cache integration that mirrors the cloud `useCreateAPITask` shape so each
 * page can pass the same optimistic config it already builds for cloud task
 * creation. When omitted, the hook still works — it just doesn't touch any
 * task list cache.
 */
export interface OptimisticTaskListConfig<TQueryData> {
  /** Query key whose data this hook should optimistically modify. */
  queryKey: readonly unknown[]
  /** Extracts the flat list of tasks from the cached query data. */
  getFlatTaskList: (data: TQueryData | undefined) => FlatTaskList | undefined
  /** Returns a new cached query data value with the given task list. */
  setFlatTaskList: (oldData: TQueryData | undefined, newList: FlatTaskList) => TQueryData
  /** Maximum number of tasks to keep in the list after the optimistic
   * placeholder is replaced; matches the page's pagination size so the list
   * doesn't overflow. */
  pageSize: number
}

export interface UseCreateLocalDeviceSessionOptions<TQueryData> {
  optimisticTaskListConfig?: OptimisticTaskListConfig<TQueryData>
}

interface MutationContext {
  clientGeneratedId: string
}

/**
 * Creates a new task in Mission Control targeting one of the user's
 * connected local devices. Mission Control resolves which active steerable
 * session in the (deviceId, workingDirectory) bucket should receive the
 * `start_session` steer command and dispatches it server-side, baking the
 * new task ID into the payload so the runtime's spawned session attaches
 * to this same task.
 *
 * Routed through CAPI to Mission Control's CreateTask endpoint.
 *
 * The local device always uses its own working directory and configured
 * model for the spawned child, so no repo/branch/model fields are sent.
 *
 * When `optimisticTaskListConfig` is provided, this hook performs the same
 * insert→replace→cleanup React Query cache dance that `useCreateAPITask`
 * does for cloud tasks, so the calling page sees a placeholder appear at
 * the top of the list immediately and get replaced with the real task once
 * Mission Control responds.
 */
export function useCreateLocalDeviceSession<TQueryData = unknown>(
  options?: UseCreateLocalDeviceSessionOptions<TQueryData>,
) {
  const queryClient = useQueryClient()
  const config = options?.optimisticTaskListConfig

  return useMutation<StartLocalDeviceSessionResult, Error, StartLocalDeviceSessionInput, MutationContext>({
    onMutate: async (input: StartLocalDeviceSessionInput): Promise<MutationContext> => {
      const clientGeneratedId = uuid()
      if (!config) return {clientGeneratedId}

      await queryClient.cancelQueries({queryKey: config.queryKey})
      const optimistic = createOptimisticLocalDeviceTask({
        clientGeneratedId,
        problemStatement: input.problemStatement,
      })
      queryClient.setQueryData<TQueryData>(config.queryKey, oldData =>
        config.setFlatTaskList(oldData, [optimistic, ...(config.getFlatTaskList(oldData) ?? [])]),
      )
      return {clientGeneratedId}
    },
    mutationFn: async (input: StartLocalDeviceSessionInput): Promise<StartLocalDeviceSessionResult> => {
      const {deviceId, workingDirectory, problemStatement} = input
      let response
      try {
        response = await fetchCAPI({
          path: '/agents/tasks',
          method: 'POST',
          body: {
            prompt: problemStatement,
            device: {device_id: deviceId, working_directory: workingDirectory},
          },
        })
      } catch (err) {
        if (err instanceof FetchCAPIError && err.status === 404) {
          throw new Error('Local device not connected')
        }
        throw err
      }

      const task = (await response.json()) as APITask
      if (!task?.id) {
        throw new Error('CreateTask response missing task id')
      }
      return {task}
    },
    onSuccess: ({task}, _input, context) => {
      if (!config || !context) return
      const transformed = transformAPITaskToUnhydrated(task)
      queryClient.setQueryData<TQueryData>(config.queryKey, oldData => {
        const cached = config.getFlatTaskList(oldData) ?? []
        const withoutOptimistic = cached.filter(t => t.clientGeneratedId !== context.clientGeneratedId)
        const next = transformed ? sortTasksByDate([transformed, ...withoutOptimistic]) : withoutOptimistic
        return config.setFlatTaskList(oldData, next.slice(0, config.pageSize))
      })
      queryClient.invalidateQueries({queryKey: config.queryKey, exact: true})
    },
    onError: (_error, _input, context) => {
      if (!config || !context) return
      queryClient.setQueryData<TQueryData>(config.queryKey, oldData => {
        const cached = config.getFlatTaskList(oldData) ?? []
        return config.setFlatTaskList(
          oldData,
          cached.filter(t => t.clientGeneratedId !== context.clientGeneratedId),
        )
      })
    },
  })
}

interface CreateOptimisticLocalDeviceTaskParams {
  clientGeneratedId: string
  problemStatement: string
}

/**
 * Build the optimistic placeholder for a local device task. We deliberately
 * leave `nwo` undefined: the runtime determines the repo when it spawns the
 * child session, so there is nothing meaningful to display until the real
 * task arrives.
 */
function createOptimisticLocalDeviceTask({
  clientGeneratedId,
  problemStatement,
}: CreateOptimisticLocalDeviceTaskParams): OptimisticTask {
  return {
    resource_type: 'pull',
    resource_id: -1,
    resource_global_id: '',
    session_count: 1,
    latest_session_updated_at: new Date().toISOString(),
    latest_session_state: 'in_progress',
    isOptimistic: true,
    clientGeneratedId,
    problemStatement,
    name: problemStatement,
  }
}
